'use client';

import { buildShopPath } from '@/features/shop/shopQuery';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Overlay, Pagination, Popover } from 'react-bootstrap';
import './shopPagination.scss';

function getResultsAnchor() {
  return (
    document.getElementById('search-results') ||
    document.getElementById('latest-products')
  );
}

export default function ShopPagination({
  page,
  pages,
  total,
  searchParams,
  pathname = '/shop',
  itemLabel = 'products',
}) {
  const router = useRouter();
  const [popShow, setPopShow] = useState(false);
  const [popTarget, setPopTarget] = useState(null);
  const [popValue, setPopValue] = useState(page);
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  const containerRef = useRef(null);

  const goToPage = (targetPage) => {
    const nextPage = Math.min(Math.max(Number(targetPage) || 1, 1), pages);

    setPopShow(false);
    router.push(buildShopPath(searchParams, { page: nextPage }, pathname));

    window.setTimeout(() => {
      getResultsAnchor()?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
  };

  useEffect(() => {
    setPopValue(page);
  }, [page]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const widthQuery = window.matchMedia('(max-width: 575.98px)');
    const orientationQuery = window.matchMedia('(orientation: portrait)');
    const updateLayout = () => {
      setIsMobilePortrait(widthQuery.matches && orientationQuery.matches);
    };

    updateLayout();
    widthQuery.addEventListener?.('change', updateLayout);
    orientationQuery.addEventListener?.('change', updateLayout);

    return () => {
      widthQuery.removeEventListener?.('change', updateLayout);
      orientationQuery.removeEventListener?.('change', updateLayout);
    };
  }, []);

  if (pages <= 1) {
    return null;
  }

  const hasPrev = page > 1;
  const hasNext = page < pages;

  const openJumpPopover = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setPopTarget(event.target);
    setPopValue(page);
    setPopShow(true);
  };

  const handleJump = (value) => {
    const nextPage = Number(value);

    if (!Number.isFinite(nextPage) || nextPage < 1 || nextPage > pages) {
      return;
    }

    goToPage(nextPage);
  };

  return (
    <div className="w-100 overflow-x-auto">
      {isMobilePortrait ? (
        <div
          ref={containerRef}
          className="d-flex flex-column align-items-center justify-content-center gap-2 flex-wrap py-2"
          aria-label="Pagination, compact"
        >
          <Pagination className="w-fit-content mb-0 mx-auto mx-sm-0">
            <Pagination.First onClick={() => goToPage(1)} disabled={!hasPrev} />
            <Pagination.Prev
              onClick={() => goToPage(page - 1)}
              disabled={!hasPrev}
            />

            {page > 2 ? (
              <>
                <Pagination.Item onClick={() => goToPage(1)}>1</Pagination.Item>
                <Pagination.Ellipsis
                  onClick={openJumpPopover}
                  aria-label="Jump to page"
                />
              </>
            ) : null}

            {page - 1 >= 1 ? (
              <Pagination.Item onClick={() => goToPage(page - 1)}>
                {page - 1}
              </Pagination.Item>
            ) : null}

            <Pagination.Item active>{page}</Pagination.Item>

            {page + 1 <= pages ? (
              <Pagination.Item onClick={() => goToPage(page + 1)}>
                {page + 1}
              </Pagination.Item>
            ) : null}

            {page < pages - 1 ? (
              <>
                <Pagination.Ellipsis
                  onClick={openJumpPopover}
                  aria-label="Jump to page"
                />
                <Pagination.Item onClick={() => goToPage(pages)}>
                  {pages}
                </Pagination.Item>
              </>
            ) : null}

            <Pagination.Next
              onClick={() => goToPage(page + 1)}
              disabled={!hasNext}
            />
            <Pagination.Last
              onClick={() => goToPage(pages)}
              disabled={!hasNext}
            />
          </Pagination>

          {typeof total === 'number' ? (
            <span className="text-muted small text-nowrap">
              {total.toLocaleString()} {itemLabel}
            </span>
          ) : null}
        </div>
      ) : (
        <div
          ref={containerRef}
          className="d-flex flex-column align-items-center justify-content-center gap-2 flex-wrap py-2"
        >
          <Pagination className="w-fit-content mb-0 mx-auto mx-sm-0">
            <Pagination.First onClick={() => goToPage(1)} disabled={!hasPrev} />
            <Pagination.Prev
              onClick={() => goToPage(page - 1)}
              disabled={!hasPrev}
            />

            {[...Array(pages).keys()]
              .map((entry) => entry + 1)
              .filter(
                (entry) =>
                  entry === 1 ||
                  entry === pages ||
                  Math.abs(entry - page) <= 2 ||
                  (entry === page - 3 && page > 4) ||
                  (entry === page + 3 && page < pages - 3),
              )
              .map((entry, index, entries) => {
                const previousEntry = entries[index - 1];

                return [
                  previousEntry && entry - previousEntry > 1 ? (
                    <Pagination.Ellipsis
                      key={`ellipsis-${entry}`}
                      onClick={openJumpPopover}
                      aria-label="Jump to page"
                    />
                  ) : null,
                  <Pagination.Item
                    key={entry}
                    active={entry === page}
                    onClick={() => goToPage(entry)}
                  >
                    {entry}
                  </Pagination.Item>,
                ];
              })}

            <Pagination.Next
              onClick={() => goToPage(page + 1)}
              disabled={!hasNext}
            />
            <Pagination.Last
              onClick={() => goToPage(pages)}
              disabled={!hasNext}
            />
          </Pagination>

          {typeof total === 'number' ? (
            <small className="text-muted text-nowrap">
              {total.toLocaleString()} {itemLabel}
            </small>
          ) : null}
        </div>
      )}

      <Overlay
        show={popShow}
        target={popTarget}
        placement="bottom"
        container={containerRef.current}
        rootClose
        onHide={() => setPopShow(false)}
      >
        <Popover id="jump-to-page-popover">
          <Popover.Body>
            <Form
              onSubmit={(event) => {
                event.preventDefault();
                handleJump(popValue);
              }}
              className="d-flex gap-2 align-items-center"
            >
              <Form.Control
                type="number"
                min={1}
                max={pages}
                value={popValue}
                onChange={(event) => setPopValue(event.target.value)}
                aria-label="Page number"
                className="w-6rem"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleJump(popValue)}
              >
                Go
              </Button>
            </Form>
          </Popover.Body>
        </Popover>
      </Overlay>
    </div>
  );
}
